import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useSafetyMetrics } from '@/hooks/use-safety';
import { useOrgTimeZone } from '@/hooks/use-settings';
import {
  formatDateTimeInTimeZone,
  formatLocalDate,
  todayInTimeZone,
} from '@/lib/date-utils';
import { parseISO, subYears } from 'date-fns';

export function SafetyMetricsSection() {
  const timeZone = useOrgTimeZone();
  const orgToday = todayInTimeZone(timeZone);
  const defaultDateFrom = formatLocalDate(subYears(parseISO(orgToday), 1));
  const [dateRange, setDateRange] = useState<{
    dateFrom: string;
    dateTo: string;
  } | null>(null);
  const dateFrom = dateRange?.dateFrom ?? defaultDateFrom;
  const dateTo = dateRange?.dateTo ?? orgToday;

  const { data: metrics, isLoading } = useSafetyMetrics({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-lg font-semibold text-gray-900">Safety Performance</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="dateFrom" className="text-sm whitespace-nowrap">From</Label>
            <Input
              id="dateFrom"
              type="date"
              value={dateFrom}
              onChange={(e) =>
                setDateRange((current) => ({
                  dateFrom: e.target.value,
                  dateTo: current?.dateTo ?? dateTo,
                }))
              }
              className="w-auto"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="dateTo" className="text-sm whitespace-nowrap">To</Label>
            <Input
              id="dateTo"
              type="date"
              value={dateTo}
              onChange={(e) =>
                setDateRange((current) => ({
                  dateFrom: current?.dateFrom ?? dateFrom,
                  dateTo: e.target.value,
                }))
              }
              className="w-auto"
            />
          </div>
        </div>
      </div>

      {/* Prominent days since last incident counter */}
      {metrics.days_since_last_incident !== null && (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-green-700 font-medium">Days Since Last Incident</p>
            <p className="text-5xl font-bold text-green-800 mt-2">
              {metrics.days_since_last_incident}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Total Incidents</p>
            <p className="text-2xl font-bold mt-1">{metrics.total_incidents}</p>
            <p className="text-xs text-gray-500 mt-1">
              {metrics.open_incidents} open
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Near-Miss Reports</p>
            <p className="text-2xl font-bold mt-1">{metrics.total_near_misses}</p>
            <p className="text-xs text-gray-500 mt-1">
              Higher is better (reporting culture)
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">JSA Completion Rate</p>
            <p className="text-2xl font-bold mt-1">
              {metrics.jsa_completion_rate.toFixed(0)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {metrics.total_jsas} total JSAs
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Avg. Days to Close</p>
            <p className="text-2xl font-bold mt-1">
              {metrics.avg_days_to_close_incidents !== null
                ? metrics.avg_days_to_close_incidents.toFixed(1)
                : '-'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Incident resolution time
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly trends */}
      {metrics.incidents_by_month.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Monthly Incidents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {metrics.incidents_by_month.map((item) => {
                const maxCount = Math.max(
                  ...metrics.incidents_by_month.map((m) => m.count),
                  1
                );
                const height = Math.max((item.count / maxCount) * 100, 4);
                return (
                  <div key={item.month} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-500">{item.count}</span>
                    <div
                      className="w-full bg-red-400 rounded-t"
                      style={{ height: `${height}%` }}
                    />
                    <span className="text-xs text-gray-400 truncate w-full text-center">
                      {formatDateTimeInTimeZone(
                        `${item.month.slice(0, 7)}-15T12:00:00Z`,
                        timeZone,
                        { month: 'short' }
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {metrics.near_misses_by_month.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Monthly Near-Misses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {metrics.near_misses_by_month.map((item) => {
                const maxCount = Math.max(
                  ...metrics.near_misses_by_month.map((m) => m.count),
                  1
                );
                const height = Math.max((item.count / maxCount) * 100, 4);
                return (
                  <div key={item.month} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-500">{item.count}</span>
                    <div
                      className="w-full bg-orange-400 rounded-t"
                      style={{ height: `${height}%` }}
                    />
                    <span className="text-xs text-gray-400 truncate w-full text-center">
                      {formatDateTimeInTimeZone(
                        `${item.month.slice(0, 7)}-15T12:00:00Z`,
                        timeZone,
                        { month: 'short' }
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
